import { PageController } from '@client/components/_core/layout/PageController';
import { TextAreaWithAiAssistant } from '@client/components/_shared/ai/TextAreaWithAiAssistant';
import { UploadControlV2 } from '@client/components/_shared/documents/UploadControlV2';
import { HandlingPartySelect } from '@client/components/_shared/selectors/common/HandlingPartySelect';
import { UserSelector } from '@client/components/_shared/selectors/users/UserSelector';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { CurrencyInput } from '@client/components/_ui/data-entry/CurrencyInput';
import { DatePicker } from '@client/components/_ui/data-entry/DatePicker';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { CoiDefs } from '@client/global/conflict';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import { type RouterInputs, trpc } from '@client/trpc/client';
import { buildCurrencyInputRules, requiredFieldRule } from '@client/utils';
import { createFileRoute } from '@tanstack/react-router';
import { Card, Divider, Form, Input, Select, Space } from 'antd';

export const Route = createFileRoute('/console/$orgId/compliance/coi/add')({
    staticData: {
        metadata: {
            title: 'New conflict of interest',
        },
        ui: {
            widthVariant: 'compact',
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <CoiAdd />
        </PageController>
    );
}

type ConflictOfInterest = RouterInputs['conflictOfInterest']['createConflictOfInterest'];

const FormItem = Form.Item<ConflictOfInterest>;

function CoiAdd() {
    const orgId = useOrgId();
    const { fDateShortDayJs } = useFormatter();
    const { mutate, isPending } = trpc.conflictOfInterest.createConflictOfInterest.useMutation();
    const { onFileListChange, files } = UploadControlV2.useUploadControlV2();
    const navigate = useNavigate();

    const [form] = Form.useForm<ConflictOfInterest>();
    const handlingPartyIds = Form.useWatch(({ handlingPartyIds }) => handlingPartyIds, form);
    // TODO: Uncomment when tRPC types are regenerated to include ownerId
    // const ownerId = Form.useWatch(({ ownerId }) => ownerId, form);

    function onFinish(values: ConflictOfInterest) {
        message.loading('Saving...');

        mutate(
            {
                ...values,
                files: files.map((item) => ({
                    key: item.key,
                    name: item.name,
                    description: item.description,
                    categories: item.categories,
                    mimeType: item.mimeType,
                    size: item.size,
                    additionalInfo: item.additionalInfo,
                })),
            },
            {
                onSuccess({ id }) {
                    message.success('Saved.');

                    navigate({
                        to: '/console/$orgId/compliance/coi/details/$id/manage',
                        params: {
                            id,
                            orgId,
                        },
                    });
                },
                onError(error) {
                    errorMessage.show(error);
                },
            },
        );
    }

    return (
        <Form
            form={form}
            layout="horizontal"
            labelCol={{ span: 10 }}
            wrapperCol={{ span: 14 }}
            onFinish={onFinish}
            scrollToFirstError
        >
            <Card>
                <Divider titlePlacement="start">Personal details</Divider>

                <FormItem
                    label="First name"
                    name="firstName"
                    rules={[{ required: true, message: 'Field is required' }]}
                >
                    <Input />
                </FormItem>
                <FormItem label="Last name" name="lastName" rules={[{ required: true, message: 'Field is required' }]}>
                    <Input />
                </FormItem>
                <FormItem label="Position" name="position">
                    <Input />
                </FormItem>
                <FormItem
                    label="Date advised"
                    name="dtAdvised"
                    rules={[{ required: true, message: 'Field is required' }]}
                >
                    <DatePicker className="w-full" format={fDateShortDayJs} maxDate={new Date()} />
                </FormItem>
                <FormItem label="Severity" name="severity" rules={[{ required: true, message: 'Field is required' }]}>
                    <Select
                        options={CoiDefs.arySeverity.map((item) => ({
                            value: item,
                        }))}
                    />
                </FormItem>

                <Divider titlePlacement="start">Conflict of interest</Divider>

                <FormItem label="Interest disclosed" name="interestDisclosed">
                    <TextAreaWithAiAssistant rows={4} relatedFieldLabel="Conflict of interest - Interest disclosed" />
                </FormItem>
                <FormItem label="Nature of potential conflict" name="natureOfConflict">
                    <TextAreaWithAiAssistant
                        rows={4}
                        relatedFieldLabel="Conflict of interest - Nature of potential conflict"
                    />
                </FormItem>
                <FormItem label="Party to be notified" name="partyToBeNotified">
                    <Input />
                </FormItem>
                <FormItem label="Estimated value" name="estimatedValue" rules={buildCurrencyInputRules()}>
                    <CurrencyInput />
                </FormItem>

                <Divider titlePlacement="start">Action and consequence management</Divider>

                <FormItem
                    label="Handling parties"
                    name="handlingPartyIds"
                    rules={[{ required: true, message: 'Field is required' }]}
                >
                    <HandlingPartySelect forOrgAdminShowAll onChange={() => form.resetFields(['ownerId'])} />
                </FormItem>
                <FormItem label="Owner" name="ownerId" rules={requiredFieldRule} validateTrigger={['onSelect']}>
                    <UserSelector
                        handlingPartyIds={handlingPartyIds}
                        limitToCurrentUserHandlingParties
                        placeholder=""
                    />
                </FormItem>
                <FormItem label="Mechanism chosen to manage the conflict" name="conflictManagement">
                    <Select
                        options={CoiDefs.aryConflictManagement.map((item) => ({
                            value: item,
                        }))}
                        mode="multiple"
                    />
                </FormItem>
                <FormItem label="Solution implemented / Action taken" name="solutionImplemented">
                    <TextAreaWithAiAssistant
                        rows={4}
                        relatedFieldLabel="Conflict of interest - Solution implemented / Action taken"
                    />
                </FormItem>
                <FormItem label="Date implemented" name="dtImplemented">
                    <DatePicker className="w-full" format={fDateShortDayJs} maxDate={new Date()} dateOnly />
                </FormItem>
                <FormItem label="Consequence management" name="consequenceManagement">
                    <TextAreaWithAiAssistant
                        rows={4}
                        relatedFieldLabel="Conflict of interest - Consequence management"
                    />
                </FormItem>
                <FormItem label="Other comments" name="comments">
                    <TextAreaWithAiAssistant rows={4} relatedFieldLabel="Conflict of interest - Other comments" />
                </FormItem>

                <Divider titlePlacement="start">Attachments</Divider>

                <UploadControlV2
                    categories={['Conflict of interest lodgement']}
                    purpose="entityAttachment"
                    onFileListChange={onFileListChange}
                />
            </Card>

            <Space className="mt-2 flex justify-end">
                <Button
                    type="secondary"
                    onClick={() =>
                        navigate({
                            to: '/console/$orgId/compliance/coi',
                            params: {
                                orgId,
                            },
                        })
                    }
                >
                    Cancel
                </Button>
                <Button type="primary" htmlType="submit" loading={isPending}>
                    Save
                </Button>
            </Space>
        </Form>
    );
}
